import React, { useCallback,useEffect,useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { COOKIE_IDS } from 'App/constants';
import { logError } from 'App/services/faroService';
import { ButtonSizes, ButtonWidth } from 'Shared/components/design/button/Button';
import LoadingSpinner from 'Shared/components/design/loadingSpinner/LoadingSpinner';
import {
  SSO_BUTTON_TEST_IDS,
  SSOButtonOptionsByProvider,
  SSOButtonProps,
  SSOProvider,
} from 'Shared/components/design/SSOButton/constants';
import { SSOButtonBtn } from 'Shared/components/design/SSOButton/style';
import { setCookie } from 'Shared/services/cookieService';
import { handleAppleLogin, handleFacebookLogin, initializeAppleSSO, initializeGoogleSSO, loadAppleSDK, loadFacebookSDK, loadGoogleSDK } from 'Shared/utils/SSOUtils';

/**
 * SSO Button component
 */
const SSOButton: React.FC<SSOButtonProps> = ({
  provider,
  onClick,
  errorCallback,
  isLoadingCallback,
  ...props
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const googleCodeClientRef       = useRef<Window["google"]["accounts"]["oauth2"]["CodeClient"] | null>(null);
  const appleInitializedRef       = useRef<Window["AppleID"]["auth"] | null>(null);
  const { t }                     = useTranslation('signIn');

  // Setting default values
  const {
    width = ButtonWidth.FULL,
    size  = ButtonSizes.LARGE,
  } = props;

  // Getting provider options
  const {
    name: providerName,
    logoUri,
    ...options
  } = SSOButtonOptionsByProvider[provider];

  /**
   * Triggers error callback with the provider name
   */
  const handleError = useCallback((): void => {
    errorCallback?.(provider);
  }, [errorCallback, provider]);


  /**
   * Initialize Google SSO on mount
   */
  useEffect(() => {
    if (provider === SSOProvider.GOOGLE) {
      if (!window.google?.accounts?.oauth2) {
        setIsLoading(true);

        loadGoogleSDK().then(() => {
          //verify SDK is available before initializing
          if (!window.google?.accounts?.oauth2) {
            throw new Error('Google SDK loaded but oauth2 not available');
          }
          googleCodeClientRef.current = initializeGoogleSSO(handleError, isLoadingCallback);
        }).catch((error) => {
          logError('Google SDK: Failed to load', {
            error,
            hasGoogleGlobal: !!window.google,
            hasOAuth2: !!window.google?.accounts?.oauth2,
            userAgent: navigator.userAgent,
          });
        }).finally(() => {
          setIsLoading(false);
        });
      } else {
        googleCodeClientRef.current = initializeGoogleSSO(handleError, isLoadingCallback);
      }
    }
  }, [provider, handleError, isLoadingCallback]);

  /**
   * Initialize Apple SSO on mount
   */
  useEffect(() => {
    if (provider === SSOProvider.APPLE) {
      if (appleInitializedRef.current) {
        return;
      }

      const initializeApple = async () => {
        if (!window.AppleID?.auth) {
          setIsLoading(true);
          try {
            await loadAppleSDK();
            initializeAppleSSO();
            appleInitializedRef.current = window.AppleID?.auth;
          } catch (error) {
            logError('Apple SDK: Failed to load', error);
            handleError();
          } finally {
            setIsLoading(false);
          }
        } else {
          initializeAppleSSO();
          appleInitializedRef.current = window.AppleID?.auth;
        }
      };

      initializeApple();
    }
  }, [provider, handleError]);

  /**
   * Initialize Facebook SSO on mount
   */
  useEffect(() => {
    if (provider === SSOProvider.FACEBOOK) {
      if (!window.FB) {
        setIsLoading(true);
        loadFacebookSDK()
        .catch((error) => {
          logError('Facebook SDK: Failed to load', {
            error,
            hasFBGlobal: !!window.FB,
            userAgent: navigator.userAgent,
          });
        }).finally(() => {
          setIsLoading(false);
        });
      }
    }
  }, [provider, handleError]);

  /**
   * Click handler
   */
  const handleClick = (): void => {
    onClick?.();
    // Setting forceV3SignUp cookie with an expiration date of 1 day
    setCookie(COOKIE_IDS.FORCE_TO_V3_FOR_NEW_SIGNUP, '1', 1, true);

    // trigger google login
    if (provider === SSOProvider.GOOGLE) {
      if (!googleCodeClientRef.current) {
        //handle error and show error modal only when user actually tries to use Google SSO
        logError('Google SSO: User attempted to use Google SSO but SDK is not available', {
          hasGoogleGlobal: !!window.google,
          hasOAuth2: !!window.google?.accounts?.oauth2,
          userAgent: navigator.userAgent,
        });
        handleError();
        return;
      }
      googleCodeClientRef.current.requestCode();
    }

    // trigger apple login
    if (provider === SSOProvider.APPLE) {
      if (!appleInitializedRef.current) {
        logError('Apple SSO: User attempted to use Apple SSO but SDK is not available', {
          hasAppleGlobal: !!window.AppleID,
          hasAuth: !!window.AppleID?.auth,
          userAgent: navigator.userAgent,
        });
        handleError();
        return;
      }
      handleAppleLogin(handleError, isLoadingCallback);
    }

    // trigger facebook login
    if (provider === SSOProvider.FACEBOOK) {
      if (!window.FB) {
        //handle error and show error modal only when user actually tries to use Facebook SSO
        logError('Facebook SSO: User attempted to use Facebook SSO but SDK is not available', {
          hasFBGlobal: !!window.FB,
          userAgent: navigator.userAgent,
        });
        handleError();
        return;
      }
      handleFacebookLogin(handleError, isLoadingCallback);
    }
  };

  return (
    <SSOButtonBtn
      size={size}
      width={width}
      options={options}
      onClick={handleClick}
      data-testid={SSO_BUTTON_TEST_IDS.CONTAINER}
      {...props}
    >
      {isLoading ? (
        <LoadingSpinner circleColor="#D3D3D3" heightWidth="28px" data-testid='sso-loading'/>
      ) : (
        <>
          <img src={logoUri} alt={providerName} />
          {t('SignInForm--SSOButton--text', { providerName })}
        </>
      )}
    </SSOButtonBtn>
  );
};

export default SSOButton;